import './assets/styles/global.css';
import { Suspense, useEffect, useMemo, useState } from 'react';
import { clientRouter } from './router/routes.tsx';
import { UseWorkspaceConfigs } from '@r/hooks/use-workspace-config.ts';
import { WorkspaceConfigContext } from '@r/context/workspace-config-context.ts';
import { RouterProvider } from '@tanstack/react-router';
import { AuthLoader, useUser } from '@r/api/auth.tsx';
import { useRoutes } from '@r/hooks/use-routes.ts';
import { Loader } from '@r/components/loader/loader.tsx';
import i18n from '@r/lib/i18n.ts';
import { useTranslation } from 'react-i18next';
import { CartContext } from '@r/context/cart-context.ts';
import { WorkspaceContext } from '@r/context/workspace-context.ts';
import { useCart } from '@r/hooks/use-cart.ts';
import { UseWorkspace } from '@r/hooks/use-workspace.ts';
import { BalanceContext } from '@r/context/balance-context.ts';
import { getActiveWorkspaceUniversal } from '@r/helpers/api.ts';
import { getClientWorkspacePageTitleSuffix } from '@r/helpers/workspace-page-title.ts';
import { getMaintenancePageReason, shouldShowMaintenancePage } from '@r/lib/maintenance-error.ts';
import { MaintenanceErrorPage } from '@r/page/error/maintenance-error-page.tsx';

const App = () => {
  const { ready } = useTranslation();
  const user = useUser();
  const workspaceData = UseWorkspace(user.data);
  const workspaceConfig = UseWorkspaceConfigs(true);
  const routes = useRoutes();
  const cart = useCart(user.data?.id, workspaceData.workspace?.id);
  const [balance, setBalance] = useState(0);
  const balanceContextValue = useMemo(() => ({ balance, updateBalance: setBalance }), [balance]);

  useEffect(() => {
    if (user.data?.visibleWorkspaces) {
      const activeWorkspace = getActiveWorkspaceUniversal(user.data.visibleWorkspaces);

      if (activeWorkspace?.company?.balance !== undefined) {
        setBalance(activeWorkspace.company.balance);
      }
    }
  }, [user.data]);

  const hasSession = localStorage.getItem('token') !== null;

  // Boot : attendre config workspace, user, i18n et routes (si connecté)
  const isLoading =
    !i18n.isInitialized ||
    !ready ||
    workspaceConfig.isLoading ||
    user.isLoading ||
    (hasSession && routes.isLoading);

  if (isLoading) {
    return <Loader />;
  }

  // Boot : 503 (maintenance) ou 500 whitelist après retries → page maintenance.
  if (workspaceConfig.isError && shouldShowMaintenancePage(workspaceConfig.error)) {
    return <MaintenanceErrorPage reason={getMaintenancePageReason(workspaceConfig.error)} />;
  }

  if (user.isError && shouldShowMaintenancePage(user.error)) {
    return <MaintenanceErrorPage reason={getMaintenancePageReason(user.error)} />;
  }

  if (hasSession && routes.isError && shouldShowMaintenancePage(routes.error)) {
    return <MaintenanceErrorPage reason={getMaintenancePageReason(routes.error)} />;
  }

  // Autres erreurs de boot (réseau, 4xx…) : message minimal, pas de maintenance
  if (workspaceConfig.isError || user.isError || (hasSession && routes.isError)) {
    return (
      <div className="flex justify-center items-center min-h-screen text-sm text-zinc-600">
        Impossible de charger l&apos;application pour le moment.
      </div>
    );
  }

  return (
    <Suspense fallback={<Loader />}>
      <WorkspaceContext.Provider value={workspaceData}>
        <WorkspaceConfigContext.Provider value={workspaceConfig.data}>
          <CartContext.Provider value={cart}>
            <BalanceContext.Provider value={balanceContextValue}>
              <AuthLoader renderLoading={() => <Loader />}>
                <RouterProvider
                  router={clientRouter}
                  context={{
                    user: user.data,
                    routes: routes.data,
                    routesLoaded: routes.isSuccess,
                    workspaceConfig: workspaceConfig.data,
                    workspacePageTitleSuffix: getClientWorkspacePageTitleSuffix(
                      workspaceConfig.data,
                      workspaceData.workspace,
                      user.data
                    )
                  }}
                />
              </AuthLoader>
            </BalanceContext.Provider>
          </CartContext.Provider>
        </WorkspaceConfigContext.Provider>
      </WorkspaceContext.Provider>
    </Suspense>
  );
};

export default App;
